import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { handleApiErrorResponse } from "@/lib/apiErrorHandler";
import {
  SmsPreferences,
  SmsPreferencesResponse,
  ReferralCodeResponse,
  AccountantInvitation,
  VatSchemesAvailableResponse,
  CurrentVatSchemeResponse,
  UpdateProfilePayload,
  ChangePasswordPayload,
} from "./type";

export const smsPreferencesQueryKey = ["smsPreferences"];
export const referralCodeQueryKey = ["referralCode"];
export const availableVatSchemesQueryKey = ["availableVatSchemes"];
export const currentVatSchemeQueryKey = ["currentVatScheme"];
export const vatDueQueryKey = ["vatDue"];

/**
 * Hook to get user SMS preferences
 */
export function useSmsPreferencesQuery() {
  return useQuery({
    queryKey: smsPreferencesQueryKey,
    queryFn: async (): Promise<SmsPreferences | null> => {
      const response = await fetch("/api/v1/users/preferences/sms");
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = (await response.json()) as SmsPreferencesResponse;
      return result.data ?? result;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to update user SMS preferences
 */
export function useUpdateSmsPreferencesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SmsPreferences): Promise<SmsPreferences> => {
      const response = await fetch("/api/v1/users/preferences/sms", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = (await response.json()) as SmsPreferencesResponse;
      return result.data ?? result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsPreferencesQueryKey });
    },
  });
}

/**
 * Hook to get user Referral Code
 */
export function useReferralCodeQuery() {
  return useQuery({
    queryKey: referralCodeQueryKey,
    queryFn: async (): Promise<string | null> => {
      const response = await fetch("/api/v1/users/referral-code");
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = (await response.json()) as ReferralCodeResponse;
      return result.data?.referralCode ?? (result as any).referralCode ?? null;
    },
    staleTime: Infinity, // Referral code never changes
  });
}

/**
 * Mutation to send accountant invite email
 */
export function useSendAccountantInviteMutation() {
  return useMutation({
    mutationFn: async (
      payload: AccountantInvitation,
    ): Promise<{ success: boolean }> => {
      const response = await fetch(
        "/api/v1/internal/organisations/accountant/invite",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            accountantName: payload.name,
            accountantEmail: payload.email,
          }),
        },
      );
      if (!response.ok) throw await handleApiErrorResponse(response);
      return { success: true };
    },
  });
}

/**
 * Hook to fetch available VAT schemes from API
 */
export function useAvailableVatSchemesQuery() {
  return useQuery({
    queryKey: availableVatSchemesQueryKey,
    queryFn: async (): Promise<string[]> => {
      const response = await fetch("/api/v1/vat-schemes/available");
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = (await response.json()) as VatSchemesAvailableResponse;
      return result.data ?? [];
    },
    retry: 1, // Retry once on failure
    staleTime: 1000 * 60 * 30, // 30 minutes — schemes rarely change
  });
}

/**
 * Hook to fetch current user's VAT scheme
 */
export function useCurrentVatSchemeQuery() {
  return useQuery({
    queryKey: currentVatSchemeQueryKey,
    queryFn: async () => {
      const response = await fetch("/api/v1/vat-schemes/current");
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = (await response.json()) as CurrentVatSchemeResponse;
      return result.data ?? null;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Mutation to update business profile
 */
export function useUpdateBusinessProfileMutation() {
  const queryClient = useQueryClient();
  const { update: updateSession } = useSession();

  return useMutation({
    mutationFn: async (payload: UpdateProfilePayload) => {
      const response = await fetch("/api/v1/users/profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["userProfile"] });
      queryClient.invalidateQueries({ queryKey: currentVatSchemeQueryKey });

      // Silently refresh session with updated user data
      updateSession({
        user: {
          fullName: data?.data?.fullName || data?.fullName,
          businessName: data?.data?.businessName || data?.businessName,
        },
      });
    },
  });
}

/**
 * Mutation to change user password
 */
export function useChangePasswordMutation() {
  return useMutation({
    mutationFn: async (payload: ChangePasswordPayload) => {
      const response = await fetch("/api/v1/users/profile/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      return response.json();
    },
  });
}

/**
 * Hook to fetch current VAT due
 */
export function useVatDueQuery(organisationId?: string) {
  return useQuery({
    queryKey: [...vatDueQueryKey, organisationId],
    queryFn: async () => {
      if (!organisationId) return null;
      const response = await fetch(
        `/api/v1/engine/vat-due?organisationId=${organisationId}`,
      );
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = await response.json();
      return result.data;
    },
    enabled: !!organisationId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Mutation to update VAT snapshot
 */
export function useUpdateVatSnapshotMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      organisationId: string;
      vatDue: number;
      vatDueDate: string;
      source: string;
    }) => {
      const response = await fetch("/api/v1/vat/snapshot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...vatDueQueryKey, variables.organisationId],
      });
      queryClient.invalidateQueries({ queryKey: ["latestCalculation"] });
    },
  });
}
